import React, { useState, Fragment } from 'react';
import styled from 'styled-components';
import pickupIcon from './images/marker-pickup.svg';
import dropoffIcon from './images/marker-dropoff.svg';
import upIcon from './images/up.svg';
import downIcon from './images/down.svg';
import parcell from './images/parcell.svg';

export type AddressProps = {
    className?: string;
    addreses: AddressElement[];
};

export type AddressElement = {
    main?: string[];
    secondary?: string | null | undefined;
    note?: string | null | undefined;
    iconType: IconType;
};

const AddressCoontainer = styled.div`
    padding: 33px 24px 24px 24px;
    display: flex;
    flex-direction: row;
`;

const MainTextContainer = styled.div`
    font-size: 14px;
    color: #111B21;
    font-family: 'graphikgett', 'Graphik LCG', Helvetica, Arial, sans-serif;
`;

const SecondaryTextContainer = styled.div`
    font-size: 14px;
    color: #647882;
    font-family: 'graphikgett', 'Graphik LCG', Helvetica, Arial, sans-serif;
`;

const IconsContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-right: 16px;
`;

const VerticalDivider = styled.div`
    background-image: linear-gradient(black 33%, rgba(255, 255, 255, 0) 0%);
    background-position: right;
    background-size: 2px 6px;
    background-repeat: repeat-y;
    min-height: 34px;
    width: 2px;
    margin: 10px 0;
    flex-grow: 1;
`;

const EmptylDivider = styled.div`
    min-height: 34px;
    margin: 10px 0;
`;

const TextsContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const generateIcons = (addresses: AddressElement[]) => {
    const items: JSX.Element[] = [];
    for (var i = 0; i < addresses.length; i++) {
        items.push(<AddressIcon type={addresses[i].iconType} />);
        if (i < addresses.length - 1) {
            items.push(<VerticalDivider />);
        }
    }
    return items;
};

const generateMainElements = (element: AddressElement) => {
    const texts = element.main;
    if (!texts || texts.length === 0) {
        return '';
    }
    const items = texts.map((mainText) => {
        return <MainTextContainer>{mainText}</MainTextContainer>;
    });

    return (
        <Fragment>
            {items}
            {element.secondary && <SecondaryTextContainer>{element.secondary}</SecondaryTextContainer>}
        </Fragment>
    );
};

const generateAddressTexts = (addresses: AddressElement[]) => {
    const items: JSX.Element[] = [];
    for (var i = 0; i < addresses.length; i++) {
        items.push(
            <div style={{ display: 'flex', flexDirection: 'column' }}>{generateMainElements(addresses[i])}</div>
        );
        if (i < addresses.length - 1) {
            items.push(<EmptylDivider />);
        }
    }
    return items;
};

export const Address = (addr: AddressProps) => {
    if (addr === null) return null;

    const icons = generateIcons(addr.addreses);
    const texts = generateAddressTexts(addr.addreses);

    return (
        <AddressCoontainer className={addr.className}>
            <IconsContainer>{icons}</IconsContainer>
            <TextsContainer>{texts}</TextsContainer>
        </AddressCoontainer>
    );
};

export enum IconType {
    Pickup = 'pickup',
    Dropoff = 'dropoff',
    Parcell = 'parcell',
    Up = 'up',
    Down = 'down',
}

type AddressIconProps = {
    type?: IconType;
    number?: number;
};

const AddressIconContainer = styled.div``;

const getIconByType = (type?: IconType) => {
    switch (type) {
        case IconType.Down:
            return downIcon;
        case IconType.Dropoff:
            return dropoffIcon;
        case IconType.Parcell:
            return parcell;
        case IconType.Pickup:
            return pickupIcon;
        case IconType.Up:
            return upIcon;
        default:
            return pickupIcon;
    }
};

const AddressIcon = (initial: AddressIconProps) => {
    const [icon] = useState<AddressIconProps>(initial);

    const pic = getIconByType(initial.type);

    return (
        <AddressIconContainer className={icon.type}>
            <img src={pic} alt='Icon' />
        </AddressIconContainer>
    );
};
